export const ONLINE_PAYMENT_TYPE = 'Online'
export const PRESENCIAL_PAYMENT_TYPE = 'Presencial'

export const STRIPE_AUTHORIZED_STATUSES = ['succeeded', 'requires_capture']
export const STRIPE_PROCESSING_STATUS = 'processing'

export const isOnlinePaymentType = (paymentMethodType) =>
  paymentMethodType === ONLINE_PAYMENT_TYPE

export const isStripePaymentAuthorized = (status) =>
  STRIPE_AUTHORIZED_STATUSES.includes(status)

export const isStripePaymentProcessing = (status) =>
  status === STRIPE_PROCESSING_STATUS

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * confirmPayment({ redirect: 'if_required' }) can return a PaymentIntent still
 * in `processing` — authorization is in flight, not a failed charge. Poll until
 * it leaves that status so the user is not told to pay again on the same intent.
 */
export const waitForStripePaymentToSettle = async (
  retrievePaymentIntent,
  clientSecret,
  { intervalMs = 2000, maxAttempts = 30 } = {}
) => {
  if (!clientSecret || typeof retrievePaymentIntent !== 'function') {
    return null
  }

  let lastIntent = null
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    await delay(intervalMs)
    const result = await retrievePaymentIntent(clientSecret)
    const paymentIntent = result?.paymentIntent || null
    if (paymentIntent) {
      lastIntent = paymentIntent
    }
    if (!isStripePaymentProcessing(paymentIntent?.status)) {
      return paymentIntent || lastIntent
    }
  }

  return lastIntent
}

export const getErrorHttpStatus = (error) =>
  Number(
    error?.status ??
      error?.response?.status ??
      error?.response?.statusCode ??
      error?.$metadata?.httpStatusCode ??
      error?.underlyingError?.response?.statusCode
  )

/**
 * After Stripe has authorized, retry schedule-appointments only for a true
 * network failure. An HTTP error code from the server is terminal: capture
 * never runs, so the user should cancel and wait for the authorization to
 * reverse instead of paying again.
 */
export const isScheduleBookingNetworkError = (error) => {
  if (!error) return false

  const status = getErrorHttpStatus(error)
  if (Number.isFinite(status) && status > 0) return false

  const message = String(error.message || '').toLowerCase()
  const underlyingMessage = String(
    error.underlyingError?.message || ''
  ).toLowerCase()
  const combined = `${message} ${underlyingMessage}`
  const name = String(error.name || '')

  return (
    name === 'TypeError' ||
    name === 'TimeoutError' ||
    name === 'AbortError' ||
    name === 'NetworkError' ||
    combined.includes('network') ||
    combined.includes('failed to fetch') ||
    combined.includes('load failed') ||
    combined.includes('timeout') ||
    combined.includes('timed out') ||
    combined.includes('offline')
  )
}

/**
 * Parse a price only when it is a real number (including 0). Empty string,
 * null, undefined, and NaN stay unknown — they must not collapse to 0.
 */
export const toFiniteAmount = (value) => {
  if (value == null || value === '') return null
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : null
}

export const getAmountToCharge = ({
  downPaymentPrice,
  finalPrice,
  fallbackPrice
} = {}) => {
  const downPayment = toFiniteAmount(downPaymentPrice)
  if (downPayment != null) return downPayment

  const resolved = toFiniteAmount(finalPrice)
  if (resolved != null) return resolved

  return toFiniteAmount(fallbackPrice)
}

export const isChargedOnlinePayment = ({
  paymentMethodType,
  amount
} = {}) => isOnlinePaymentType(paymentMethodType) && toFiniteAmount(amount) > 0

/**
 * Charged online payments must never call schedule-appointments without a
 * PaymentIntent id. Membership benefits, known $0 totals, and in-person
 * methods can. A missing/empty amount is not $0 — Online still needs Stripe.
 */
export const canScheduleWithoutPaymentIntent = ({
  paymentMethodType,
  amount,
  isMembershipBenefit = false
} = {}) => {
  if (isMembershipBenefit) return true
  if (isOnlinePaymentType(paymentMethodType)) {
    return toFiniteAmount(amount) === 0
  }
  return true
}

export const canCallScheduleAppointments = ({
  paymentMethodType,
  amount,
  paymentIntentId,
  isMembershipBenefit = false
} = {}) => {
  if (
    canScheduleWithoutPaymentIntent({
      paymentMethodType,
      amount,
      isMembershipBenefit
    })
  ) {
    return true
  }
  return Boolean(paymentIntentId)
}

/**
 * Stripe return_url already includes `payment_intent`. Prefer that (or the
 * SDK id after confirmPayment) over anything persisted in storage.
 */
export const resolveSchedulePaymentIntentId = ({
  urlPaymentIntent,
  confirmedPaymentIntentId
} = {}) => urlPaymentIntent || confirmedPaymentIntentId || null

export const shouldGoToStripeCheckout = ({
  paymentMethodType,
  amount,
  isVirtual = false
} = {}) => {
  if (isChargedOnlinePayment({ paymentMethodType, amount })) return true
  if (paymentMethodType === PRESENCIAL_PAYMENT_TYPE) return false
  // Only a known $0 total skips checkout. Number('') === 0, so missing
  // prices must not be treated as free.
  if (toFiniteAmount(amount) === 0) return false
  if (isOnlinePaymentType(paymentMethodType)) return true
  return Boolean(isVirtual && isOnlinePaymentType(paymentMethodType))
}

export const buildScheduleAppointmentsBody = ({
  payload = {},
  paymentIntentId,
  comingFromGuestFlow = false
} = {}) => {
  const discounts = payload.discounts
  const reason = Array.isArray(payload.reason)
    ? payload.reason.filter((item) => typeof item === 'string' && item.trim())
    : []
  const body = {
    doctorRef: payload.doctorId,
    clinicRef: payload.clinicId,
    serviceId: payload.serviceId,
    appointments: payload.appointments,
    reason,
    discounts:
      discounts === '' || discounts == null
        ? []
        : Array.isArray(discounts)
          ? discounts
          : [discounts],
    wantToChangeDoctor: payload.wantToChangeDoctor,
    paymentMethod: payload.paymentMethod,
    wantsEarlierAppointment: payload.wantsEarlierAppointment
  }

  if (paymentIntentId) {
    body.paymentIntentId = paymentIntentId
  }

  if (comingFromGuestFlow) {
    body.comingFromGuestFlow = true
  }

  return body
}

/**
 * Packages book several appointments. Confirmation "Ver detalle" must go to
 * /citas, not /cita/:firstId — so package confirmation never exposes a single id.
 */
export const isSingleSessionBooking = (numberSessions) =>
  Number(numberSessions) === 1

export const getScheduleConfirmationAppointmentId = ({
  bookedIds = [],
  numberSessions,
  fallbackId = ''
} = {}) => {
  if (!isSingleSessionBooking(numberSessions)) return ''
  return bookedIds[0] || fallbackId || ''
}

export const getScheduleConfirmationPath = (appointmentId) =>
  appointmentId
    ? `/confirmacionAgendar?appointmentId=${appointmentId}`
    : '/confirmacionAgendar'

export const getScheduleDetailsPath = ({
  isPackage = false,
  appointmentId = '',
  appointmentIdFromUrl = ''
} = {}) => {
  if (isPackage) return '/citas'
  if (appointmentId) return `/cita/${appointmentId}`
  if (appointmentIdFromUrl) return `/cita/${appointmentIdFromUrl}`
  return '/citas'
}

export const ONLINE_BOOKING_PAYLOAD_KEY = 'virtualApptInfo'

export const hasOnlineBookingAppointments = (payload) =>
  Array.isArray(payload?.appointments) && payload.appointments.length > 0

/**
 * getPatientInfo writes the patient record into the same `dataPatient` state
 * the checkout flow uses for slot/token payload. Keep the booking payload.
 */
export const keepOnlineBookingPayload = (currentPayload, incomingPayload) =>
  hasOnlineBookingAppointments(currentPayload)
    ? currentPayload
    : incomingPayload

/** Prefer in-memory slots; if getPatientInfo clobbered them, use storage. */
export const resolveOnlineBookingPayload = (currentPayload) => {
  if (hasOnlineBookingAppointments(currentPayload)) return currentPayload
  const stored = readOnlineBookingPayload()
  if (hasOnlineBookingAppointments(stored)) return stored
  return currentPayload || stored || null
}

export const readOnlineBookingPayload = () => {
  try {
    const raw = window.localStorage.getItem(ONLINE_BOOKING_PAYLOAD_KEY)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

export const persistOnlineBookingPayload = (payload) => {
  if (!payload) return
  try {
    const stored = readOnlineBookingPayload()
    // Pay / 3DS persist must not replace a slot payload with a patient record.
    if (
      hasOnlineBookingAppointments(stored) &&
      !hasOnlineBookingAppointments(payload)
    ) {
      return
    }
    // comingFromGuestFlow is a live session claim, not durable booking state.
    const { comingFromGuestFlow: _ignored, ...safePayload } = payload
    window.localStorage.setItem(
      ONLINE_BOOKING_PAYLOAD_KEY,
      JSON.stringify(safePayload)
    )
  } catch {
    // ignore quota errors
  }
}

